import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type {
  CosmeticOption,
  CustomizationPayload,
  ProfileCustomization,
  ProfileHeaderData,
} from '../lib/types';
import { Badge } from '../components/badges/Badge';
import { Ribbon } from '../components/ribbons/Ribbon';
import { TrainerCard } from '../components/profile/TrainerCard';
import { ProfileHeaderPanels } from '../components/profile/ProfileHeader';
import { HeaderEditor } from '../components/profile/HeaderEditor';
import { AvatarPicker } from '../components/avatar/AvatarPicker';
import { Button, ErrorState, LoadingGrid, Panel, Tabs } from '../components/ui';

/**
 * Profile customization.
 *
 * Two rules shape this page, and both come from CLAUDE.md.
 *
 * **Everything is earned and nothing is sold.** So locked options are shown
 * with the sentence that says how to get them, rather than hidden or priced.
 * The list comes from the server's `availableCosmetics`, which is the same call
 * the save endpoint checks against — a picker offering something the save
 * refuses is impossible here by construction, not by two people remembering.
 *
 * **What you see is what visitors see.** The preview is the real `TrainerCard`,
 * not a mock-up of one. An editor that draws its own approximation of a
 * nameplate is a second implementation of that nameplate, and the two drift the
 * first time either changes.
 *
 * One draft, one Save. A per-field autosave would fire a request per click on a
 * screen where somebody will click twenty times deciding, and "Save" is a
 * clearer promise than a spinner that appears whenever you change your mind.
 */

type Tab = 'trainer' | 'header' | 'look' | 'layout' | 'favourites';

export function CustomizePage() {
  const { push } = useToast();
  const { me, refresh } = useSession();
  const state = useAsync<CustomizationPayload>(() => api.get('/me/customization'), []);

  const [draft, setDraft] = useState<ProfileCustomization | null>(null);
  // Opens on the trainer sprite, because that is the thing on this page a
  // player came to change — everything else is trim around it.
  const [tab, setTab] = useState<Tab>('trainer');
  // The backdrop lives on `Profile.background`, not on the customization row,
  // so it is its own bit of draft state and saves through its own endpoint.
  const [background, setBackground] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state.data) setDraft(state.data.customization);
  }, [state.data]);

  // Seeded once from the session rather than from the customization payload,
  // because the backdrop is not on that row — it is `Profile.background`.
  useEffect(() => {
    if (me && background === null) setBackground(me.profile.background);
  }, [me, background]);

  // "Has anything changed" drives the Save button. Comparing serialised drafts
  // is crude and exactly right for a form this small — it costs nothing and it
  // cannot disagree with itself the way a dirty flag per field can.
  const dirty = useMemo(
    () =>
      Boolean(
        draft &&
          state.data &&
          (JSON.stringify(draft) !== JSON.stringify(state.data.customization) ||
            (background !== null && background !== me?.profile.background)),
      ),
    [draft, state.data, background, me],
  );

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data || !draft) return <LoadingGrid count={4} height={140} />;

  const { options } = state.data;
  const set = <K extends keyof ProfileCustomization>(key: K, value: ProfileCustomization[K]) =>
    setDraft({ ...draft, [key]: value });

  async function save() {
    if (!draft) return;
    setBusy(true);
    try {
      // The avatar frame is saved through `PATCH /me/profile`, not here.
      //
      // It is stored on `Profile.frame` rather than on the customization row —
      // one column for "the ring", the one badges already grant — and only that
      // endpoint checks it against `availableFrames`. It used to be sent in
      // this payload, where the schema silently dropped it, so the picker
      // appeared to work and changed nothing.
      const profilePatch: Record<string, string> = {};
      if (draft.frame !== state.data?.customization.frame) profilePatch.frame = draft.frame;
      if (background && background !== me?.profile.background) profilePatch.background = background;
      if (Object.keys(profilePatch).length > 0) await api.patch('/me/profile', profilePatch);

      // The frame is dropped: it was saved above, through the endpoint that
      // owns it.
      const { frame: _frame, ...payload } = draft;
      const result = await api.patch<CustomizationPayload>('/me/customization', payload);
      state.setData(result);
      setDraft(result.customization);
      push('success', 'Profile updated.');
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  const featured = options.companions.find((row) => row.id === draft.featuredPokemonId) ?? null;

  /**
   * The header exactly as it will look once saved.
   *
   * Composed from the *draft* rather than from the saved payload, so every
   * click updates the preview immediately. The rules it follows are not
   * reimplemented here: which entries exist comes from `headerOptions`, and
   * what an empty block falls back to comes from `headerOptions.defaults`,
   * which the server derives with the same functions the profile uses. This
   * function only looks things up by id.
   */
  const previewHeader = ((): ProfileHeaderData => {
    const ho = state.data!.headerOptions;
    const pick = <T,>(ids: string[], from: T[], idOf: (item: T) => string): T[] =>
      ids.map((id) => from.find((item) => idOf(item) === id)).filter((item): item is T => Boolean(item));

    const badgeKeys = draft.featuredBadges.length ? draft.featuredBadges : ho.defaults.badges;
    const ribbonIds = draft.featuredRibbons.length ? draft.featuredRibbons : ho.defaults.ribbons;
    const accKeys = draft.featuredAccolades.length ? draft.featuredAccolades : ho.defaults.accolades;
    const statKeys = draft.featuredStats.length ? draft.featuredStats : ho.defaults.stats;

    return {
      badges: pick(badgeKeys, ho.badges, (badge) => badge.key),
      ribbons: pick(ribbonIds, ho.ribbons, (ribbon) => ribbon.id),
      accolades: pick(accKeys, ho.accolades, (entry) => entry.key),
      stats: pick(statKeys, ho.stats, (stat) => stat.key).map((stat) => ({
        key: stat.key,
        label: stat.label,
        format: stat.format,
        value: stat.value,
      })),
      usingDefaults: {
        badges: draft.featuredBadges.length === 0,
        ribbons: draft.featuredRibbons.length === 0,
        accolades: draft.featuredAccolades.length === 0,
        stats: draft.featuredStats.length === 0,
      },
      totals: state.data!.header.totals,
    };
  })();

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Customise your profile</h1>
          <div className="page-head__sub">
            Everything here is earned by playing. None of it is for sale.
          </div>
        </div>
        <div className="row">
          {dirty && (
            <Button variant="ghost" onClick={() => {
                setDraft(state.data!.customization);
                setBackground(me?.profile.background ?? null);
              }} disabled={busy}>
              Discard
            </Button>
          )}
          <Button variant="gold" onClick={() => void save()} loading={busy} disabled={!dirty}>
            {dirty ? 'Save' : 'Saved'}
          </Button>
        </div>
      </div>

      {/* The preview sticks to the top of the viewport while the options scroll
          under it. Every control on this page changes this card, and a preview
          you have to scroll back up to check is a preview nobody uses. */}
      <div className="customise">
        <div className="customise__preview">
          <Panel title="Preview" hint="Exactly what visitors see">
            <TrainerCard
              profile={{
                trainerName: me?.profile.trainerName ?? 'Trainer',
                avatarSprite: me?.profile.avatarSprite ?? null,
                avatar: me?.profile.avatar ?? null,
              }}
              customization={draft}
              featured={
                featured
                  ? {
                      sprite: featured.sprite,
                      displayName: featured.displayName,
                      bond: { level: featured.bondLevel, isSoulbound: featured.isSoulbound },
                    }
                  : null
              }
            >
              <div className="tiny muted">
                {me?.profile.title} · Level {me?.profile.level ?? 1}
              </div>
            </TrainerCard>

            {/* The header's four blocks, live. Rendered by the *same*
                component the profile uses, so the preview cannot draw its own
                approximation of a block and drift from the real one — which is
                the reason `TrainerCard` was extracted in the first place. */}
            <div style={{ marginTop: 'var(--gap-sm)' }}>
              <ProfileHeaderPanels
                header={previewHeader}
                username={me?.profile.username ?? ''}
                isSelf
                badgeLayout={draft.badgeLayout}
              />
            </div>
          </Panel>
        </div>

        <div className="customise__controls stack">
          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { value: 'trainer', label: 'Trainer Avatar' },
              { value: 'header', label: 'Profile Header' },
              { value: 'look', label: 'Look' },
              { value: 'layout', label: 'Layout' },
              { value: 'favourites', label: 'Favourites' },
            ]}
          />

          {/* The avatar saves on its own button rather than joining this
              page's one draft.
              That is deliberate and it is not an inconsistency: everything else
              here is a cosmetic key on one row, saved together because they are
              chosen together. An avatar is an identity, chosen from a browsable
              catalog with its own paging and filters, and folding it into a
              shared "Save" would mean a player who filtered, searched, previewed
              and clicked Select still had unsaved changes — which is exactly how
              somebody navigates away believing they picked a trainer. */}
          {tab === 'trainer' && (
            <Panel raised={false}>
              <div className="stack">
                <p className="tiny muted" style={{ margin: 0 }}>
                  Your trainer sprite appears on your profile, your trainer card, guild
                  rosters and anywhere else your name is shown. Choosing one saves
                  immediately.
                </p>
                <AvatarPicker onSelected={() => void refresh()} />
              </div>
            </Panel>
          )}

          {tab === 'look' && (
            <Panel raised={false}>
              <div className="stack">
                <CosmeticRow
                  label="Nameplate"
                  entries={options.nameplates}
                  value={draft.nameplate}
                  onChange={(key) => set('nameplate', key)}
                  render={(entry) => (
                    <span className={`nameplate nameplate--${entry.key}`}>
                      <span className="nameplate__name">{entry.name}</span>
                    </span>
                  )}
                />
                <CosmeticRow
                  label="Card border"
                  entries={options.cardBorders}
                  value={draft.cardBorder}
                  onChange={(key) => set('cardBorder', key)}
                  render={(entry) => (
                    <span className={`borderswatch tcard--border-${entry.key}`} aria-hidden />
                  )}
                />
                <CosmeticRow
                  label="Theme"
                  entries={options.themes}
                  value={draft.theme}
                  onChange={(key) => set('theme', key)}
                  render={(entry) => (
                    <span className={`themeswatch themeswatch--${entry.key}`} aria-hidden />
                  )}
                />

                {/* Frames come from badge tiers and finished museum wings, so
                    the list *is* what you have earned — there is nothing to
                    grey out, and every entry carries where it came from. */}
                <PickerRow label="Avatar frame">
                  {options.frames.map((entry) => (
                    <button
                      key={entry.frame}
                      type="button"
                      className={`picker picker--frame ${draft.frame === entry.frame ? 'picker--on' : ''}`}
                      onClick={() => set('frame', entry.frame)}
                      title={entry.from}
                    >
                      <span className="frame" data-frame={entry.frame}>
                        <span className="pxwell picker__frame-dot" />
                      </span>
                    </button>
                  ))}
                </PickerRow>
              </div>
            </Panel>
          )}

          {tab === 'header' && (
            <HeaderEditor options={state.data.headerOptions} draft={draft} onChange={set} />
          )}

          {tab === 'layout' && (
            <Panel raised={false}>
              <div className="stack">
                <Choice
                  label="Featured badges"
                  value={draft.badgeLayout}
                  onChange={(value) => set('badgeLayout', value as ProfileCustomization['badgeLayout'])}
                  options={[
                    { value: 'cabinet', label: 'Cards', hint: 'Artwork, name and tier.' },
                    { value: 'grid', label: 'Grid', hint: 'Artwork only — fits twice as many.' },
                    { value: 'row', label: 'Single row', hint: 'One line, scrolls sideways.' },
                  ]}
                />
                <Choice
                  label="My Team"
                  value={draft.teamLayout}
                  onChange={(value) =>
                    set('teamLayout', value as ProfileCustomization['teamLayout'])
                  }
                  options={[
                    { value: 'team', label: 'Team', hint: 'Six equal cards.' },
                    { value: 'podium', label: 'Podium', hint: 'The first one raised.' },
                    { value: 'parade', label: 'Parade', hint: 'A staggered line.' },
                  ]}
                />

                {/* The page backdrop. It lived on the Settings page, which is
                    where you go to change your password — every other thing
                    that decides how a profile looks is on this screen, and a
                    player looking for the background looks here. Saved through
                    `PATCH /me/profile`, which owns the column. */}
                <Choice
                  label="Profile background"
                  value={background ?? 'meadow'}
                  onChange={setBackground}
                  options={[
                    { value: 'meadow', label: 'Meadow' },
                    { value: 'dusk', label: 'Dusk' },
                    { value: 'cavern', label: 'Cavern' },
                    { value: 'shore', label: 'Shore' },
                    { value: 'library', label: 'Library' },
                  ]}
                />
              </div>
            </Panel>
          )}

          {tab === 'favourites' && (
            <Panel raised={false}>
              <div className="stack">
                <p className="tiny muted" style={{ margin: 0 }}>
                  A favourite is a preference, not a lock. Trading away a favourited Pokémon is
                  allowed — the favourite simply stops showing.
                </p>

                <PickerRow label="Featured companion">
                  <ClearButton
                    active={!draft.featuredPokemonId}
                    onClick={() => set('featuredPokemonId', null)}
                  />
                  {options.companions.map((companion) => (
                    <button
                      key={companion.id}
                      type="button"
                      className={`picker ${draft.featuredPokemonId === companion.id ? 'picker--on' : ''}`}
                      onClick={() => set('featuredPokemonId', companion.id)}
                      title={companion.displayName}
                    >
                      <img src={companion.sprite} alt="" className="pixel" width={40} height={40} />
                    </button>
                  ))}
                </PickerRow>
                {options.companions.length === 0 && (
                  <p className="tiny muted" style={{ margin: 0 }}>
                    Put some Pokémon in a Grove or on your Team and they will appear here.
                  </p>
                )}

                {options.badges.length > 0 && (
                  <PickerRow label="Favourite badge">
                    <ClearButton
                      active={!draft.favoriteBadgeKey}
                      onClick={() => set('favoriteBadgeKey', null)}
                    />
                    {options.badges.map((badge) => (
                      <button
                        key={badge.key}
                        type="button"
                        className={`picker ${draft.favoriteBadgeKey === badge.key ? 'picker--on' : ''}`}
                        onClick={() => set('favoriteBadgeKey', badge.key)}
                        title={badge.name}
                      >
                        <Badge badge={{ ...badge, tierName: null }} size={40} still />
                      </button>
                    ))}
                  </PickerRow>
                )}

                {options.ribbons.length > 0 && (
                  <PickerRow label="Favourite ribbon">
                    <ClearButton
                      active={!draft.favoriteRibbonId}
                      onClick={() => set('favoriteRibbonId', null)}
                    />
                    {options.ribbons.map((ribbon) => (
                      <button
                        key={ribbon.id}
                        type="button"
                        className={`picker ${draft.favoriteRibbonId === ribbon.id ? 'picker--on' : ''}`}
                        onClick={() => set('favoriteRibbonId', ribbon.id)}
                        title={`${ribbon.name} — ${ribbon.on}`}
                      >
                        <Ribbon ribbon={ribbon} size={32} />
                      </button>
                    ))}
                  </PickerRow>
                )}

                {options.collections.length > 0 && (
                  <PickerRow label="Favourite collection">
                    <ClearButton
                      active={!draft.favoriteCollectionKey}
                      onClick={() => set('favoriteCollectionKey', null)}
                    />
                    {options.collections.map((wing) => (
                      <button
                        key={wing.key}
                        type="button"
                        className={`chip ${draft.favoriteCollectionKey === wing.key ? 'chip--on' : ''}`}
                        onClick={() => set('favoriteCollectionKey', wing.key)}
                      >
                        {wing.name} {wing.complete ? '✓' : `· ${wing.owned}`}
                      </button>
                    ))}
                  </PickerRow>
                )}

                {options.groves.length > 0 && (
                  <PickerRow label="Favourite Grove">
                    <ClearButton
                      active={!draft.favoriteGroveId}
                      onClick={() => set('favoriteGroveId', null)}
                    />
                    {options.groves.map((grove) => (
                      <button
                        key={grove.id}
                        type="button"
                        className={`chip ${draft.favoriteGroveId === grove.id ? 'chip--on' : ''}`}
                        onClick={() => set('favoriteGroveId', grove.id)}
                      >
                        {grove.name}
                      </button>
                    ))}
                  </PickerRow>
                )}

                {options.decorations.length > 0 && (
                  <PickerRow label="Favourite decoration">
                    <ClearButton
                      active={!draft.favoriteDecorationKey}
                      onClick={() => set('favoriteDecorationKey', null)}
                    />
                    {options.decorations.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={`chip ${draft.favoriteDecorationKey === item.key ? 'chip--on' : ''}`}
                        onClick={() => set('favoriteDecorationKey', item.key)}
                      >
                        {item.name}
                      </button>
                    ))}
                  </PickerRow>
                )}
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Pieces
   ------------------------------------------------------------------------- */

/**
 * A row of cosmetic options, locked ones included.
 *
 * A locked option is shown at the same size with its requirement, greyed and
 * unclickable — the same decision the Badges page makes about unearned badges.
 * Filtering them out would leave a shorter list that teaches nobody what there
 * is to work towards.
 */
function CosmeticRow({
  label,
  entries,
  value,
  onChange,
  render,
}: {
  label: string;
  entries: CosmeticOption[];
  value: string;
  onChange: (key: string) => void;
  render: (entry: CosmeticOption) => React.ReactNode;
}) {
  const unlocked = entries.filter((entry) => entry.unlocked);
  const locked = entries.filter((entry) => !entry.unlocked);

  return (
    <div className="stack-sm">
      <div className="row row--between">
        <span className="tiny mono muted">{label.toUpperCase()}</span>
        <span className="tiny muted">
          {unlocked.length} of {entries.length}
        </span>
      </div>

      <div className="row row--wrap">
        {unlocked.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={`picker picker--swatch ${value === entry.key ? 'picker--on' : ''}`}
            onClick={() => onChange(entry.key)}
            title={entry.description}
          >
            {render(entry)}
          </button>
        ))}
      </div>

      {locked.length > 0 && (
        <details className="locked">
          <summary className="tiny muted">{locked.length} still to earn</summary>
          <div className="row row--wrap">
            {locked.map((entry) => (
              <span key={entry.key} className="picker picker--swatch picker--locked" title={entry.requirement}>
                {render(entry)}
                <span className="picker__lock tiny">{entry.name}</span>
                <span className="picker__req tiny muted">{entry.requirement}</span>
              </span>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function PickerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="stack-sm">
      <div className="tiny mono muted">{label.toUpperCase()}</div>
      <div className="row row--wrap">{children}</div>
    </div>
  );
}

function ClearButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`chip ${active ? 'chip--on' : ''}`} onClick={onClick}>
      None
    </button>
  );
}

function Choice({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; hint?: string }>;
}) {
  return (
    <div className="stack-sm">
      <div className="tiny mono muted">{label.toUpperCase()}</div>
      <div className="layout-choice">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`layout-choice__option ${value === option.value ? 'layout-choice__option--on' : ''}`}
            onClick={() => onChange(option.value)}
          >
            <span className={`layout-choice__art layout-choice__art--${option.value}`} aria-hidden />
            <strong className="tiny">{option.label}</strong>
            {option.hint && <span className="tiny muted">{option.hint}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
